import { useCallback, useState } from 'react';

import { toast } from 'material-react-toastify';

import { Select } from '@/components/shared/Select/Select';
import { TextField } from '@/components/shared/TextField/TextField';
import WithLoading from '@/lib/hoc/WithLoading/WithLoading';
import { tariffKeys } from '@/lib/query/keys/tariff';
import { Market } from '@addmile/backend-client';
import { useQueryClient } from '@tanstack/react-query';

import { ChangeLtvPopup } from './components/ChangeLtvPopup/ChangeLtvPopup';
import SyncLTVButton from './components/SyncLTVButton/SyncLTVButton';
import TariffsTable from './components/TariffsTable/TariffsTable';
import { useDebouncedSearch } from './hooks/useDebouncedSearch';
import { TariffType } from './hooks/useTariffs';
import { useTariffs } from './hooks/useTariffs';
import { useChangeTariffLTV } from './hooks/useTariffsMutations';

const Tariffs = () => {
  const queryClient = useQueryClient();
  const [tariffType, setTariffType] = useState<TariffType>(TariffType.Subscription);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedTariffId, setSelectedTariffId] = useState<number | undefined>(undefined);
  const [selectedMarket, setSelectedMarket] = useState<Market | undefined>(undefined);
  const { searchTerm, debouncedValue, handleSearchChange } = useDebouncedSearch();

  const { data: tariffs = [], isLoading, error } = useTariffs(tariffType, debouncedValue);
  const changeLTVMutation = useChangeTariffLTV();

  if (error) {
    toast.error('Failed to fetch tariffs');
  }

  const onChangeTariffType = useCallback(
    (value: string | null) => {
      setTariffType(value as TariffType);
      handleSearchChange('');
    },
    [handleSearchChange],
  );

  const onChangeLTV = useCallback((tariffId: number, market: Market) => {
    setIsPopupOpen(true);
    setSelectedTariffId(tariffId);
    setSelectedMarket(market);
  }, []);

  const onClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedTariffId(undefined);
    setSelectedMarket(undefined);
  };

  const handleConfirm = async (newLtv: string) => {
    if (!selectedTariffId || !selectedMarket) return;

    changeLTVMutation.mutate(
      {
        tariffId: selectedTariffId,
        market: selectedMarket,
        newLtv,
      },
      {
        onSuccess: () => {
          toast.success('LTV changed successfully');
          queryClient.invalidateQueries({ queryKey: tariffKeys.type(tariffType) });
          onClosePopup();
        },
        onError: (error) => {
          console.warn(error);
          toast.error('Failed to change LTV');
        },
      },
    );
  };

  const selectedTariff = selectedTariffId
    ? tariffs.find((tariff) => tariff.id === selectedTariffId)
    : undefined;

  return (
    <div className="animation-fade-in">
      <div className="flex gap-4 max-w-screen-2xl">
        <div className="flex gap-4 items-center">
          <Select
            options={[
              { value: TariffType.OneTimePurchase, label: 'One Time Purchase Tariffs' },
              { value: TariffType.Subscription, label: 'Subscription Tariffs' },
            ]}
            className="!text-neutral-80"
            value={tariffType as string}
            onChange={onChangeTariffType}
          />
          <TextField
            type="text"
            className="max-w-96"
            inputClassName="py-1 text-neutral-80 !font-normal"
            placeholder="Search by tariff name..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {tariffType === TariffType.Subscription && <SyncLTVButton />}
      </div>

      <WithLoading isLoading={isLoading} size={0.4} color="white">
        <TariffsTable tariffs={tariffs} onChangeLTV={onChangeLTV} tariffType={tariffType} />
      </WithLoading>

      {selectedTariffId && selectedTariff && (
        <ChangeLtvPopup
          isOpen={isPopupOpen}
          selectedTariff={selectedTariff}
          selectedMarket={selectedMarket as Market}
          closePopup={onClosePopup}
          confirmText="Yes, change ltv"
          handleConfirm={handleConfirm}
        />
      )}
    </div>
  );
};

export default Tariffs;
